import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type SettingsModalTab = "general" | "integrations" | "security";

interface UIState {
  isSidebarOpen: boolean;
  isSidebarOpenDesktop: boolean;
  isChatSuggestionsExpanded: boolean;
  messageInputTextValue: string;
  messageInputText: HTMLTextAreaElement | null;
  messageInputFile: HTMLInputElement | null;
  messageForm: HTMLFormElement | null;
  // supportDialogMode: "home" | "support-request" | null;
  supportDialogOptions: SupportDialogOptions;
  isDevToolsOpen: boolean;
  isChatSplitScreenOpen: boolean;
  globalConfirmDialog: {
    content: string | React.ReactElement;
    title: string;
    confirmButtonText?: string | false;
    cancelButtonText?: string | false;
    maxWidth?: "xs" | "sm" | "md" | "xl";
    fullWidth?: boolean;
    resolve?: ((accept: boolean) => void) | null;
  };
  settingsModal: {
    open: boolean;
    tab: SettingsModalTab;
    highlightAppId?: string;
  };
}

type SupportDialogOptions =
  | {
      mode: "home" | null;
    }
  | {
      mode: "support-request";
      initialValues?: {
        message: string;
      };
    };

interface UIActions {
  setMessageInputText: (element: HTMLTextAreaElement) => void;
  setMessageInputTextValue: (value: string) => void;
  setMessageInputFile: (element: HTMLInputElement) => void;
  setMessageForm: (element: HTMLFormElement) => void;
  toggleSidebar: (open: boolean) => void;
  toggleSidebarDesktop: (open: boolean) => void;
  toggleSupportDialogMode: (options: SupportDialogOptions) => void;
  toggleChatSplitScreen: (open: boolean) => void;
  setChatSuggestionsExpanded: (expanded: boolean) => void;
  toggleDevTools: (open: boolean) => void;
  confirmDialog: (
    params: Omit<UIState["globalConfirmDialog"], "resolve">,
  ) => Promise<boolean>;
  openSettingsModal: (
    tab: SettingsModalTab,
    options?: { highlightAppId?: string },
  ) => void;
  closeSettingsModal: () => void;
}

const initState: UIState = {
  isSidebarOpen: false,
  isSidebarOpenDesktop: true,
  isChatSuggestionsExpanded: true,
  isDevToolsOpen: false,
  isChatSplitScreenOpen: false,
  messageInputTextValue: "",
  messageInputText: null,
  messageInputFile: null,
  messageForm: null,
  // supportDialogMode: null,
  supportDialogOptions: { mode: null },
  globalConfirmDialog: {
    content: "",
    title: "",
    confirmButtonText: "",
    cancelButtonText: "",
    resolve: null,
  },
  settingsModal: { open: false, tab: "general" as SettingsModalTab },
};

export const useUIStore = create<UIState & UIActions>()(
  persist(
    (set) => ({
      ...initState,
      setMessageInputText: (element) =>
        set(() => ({
          messageInputText: element,
        })),
      setMessageForm: (element) =>
        set(() => ({
          messageForm: element,
        })),
      setMessageInputTextValue: (value) =>
        set(() => ({
          messageInputTextValue: value,
        })),
      setMessageInputFile: (element) =>
        set(() => ({
          messageInputFile: element,
        })),
      toggleSidebar: (open) =>
        set(() => ({
          isSidebarOpen: open,
        })),
      toggleSidebarDesktop: (open) =>
        set(() => ({
          isSidebarOpenDesktop: open,
        })),
      toggleSupportDialogMode: (options) =>
        set((state) => ({
          ...state,
          supportDialogOptions: {
            ...initState.supportDialogOptions,
            ...options,
          },
        })),
      toggleDevTools: (open) =>
        set(() => ({
          isDevToolsOpen: open,
        })),
      toggleChatSplitScreen: (open) =>
        set(() => ({
          isChatSplitScreenOpen: open,
        })),
      setChatSuggestionsExpanded: (expanded) =>
        set(() => ({
          isChatSuggestionsExpanded: expanded,
        })),
      openSettingsModal: (tab, options) =>
        set(() => ({
          settingsModal: {
            open: true,
            tab,
            highlightAppId: options?.highlightAppId,
          },
        })),
      closeSettingsModal: () =>
        set(() => ({
          settingsModal: { open: false, tab: "general" as SettingsModalTab },
        })),
      confirmDialog: async (params) => {
        const res = await new Promise<boolean>((resolve) => {
          set((state) => ({
            ...state,
            globalConfirmDialog: {
              ...state.globalConfirmDialog,
              title: params.title,
              content: params.content,
              confirmButtonText: params.confirmButtonText,
              cancelButtonText: params.cancelButtonText,
              maxWidth: params.maxWidth,
              fullWidth: params.fullWidth,
              resolve,
            },
          }));
        });
        set((state) => ({
          ...state,
          globalConfirmDialog: {
            ...state.globalConfirmDialog,
            resolve: null,
          },
        }));
        return res;
      },
    }),
    {
      name: "@sidekick:ui-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isSidebarOpenDesktop: state.isSidebarOpenDesktop,
      }),
    },
  ),
);
